import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function VolumeCalculator() {
  const [side, setSide] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [radius, setRadius] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateCube = () => {
    const s = parseFloat(side);
    if (isNaN(s) || s <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid positive number.",
        variant: "destructive",
      });
      return;
    }
    const volume = s * s * s;
    setResult(volume);
    toast({ title: "Volume Calculated", description: `V = ${volume.toFixed(2)} units³` });
  };

  const calculateBox = () => {
    const l = parseFloat(length);
    const w = parseFloat(width);
    const h = parseFloat(height);
    if (isNaN(l) || isNaN(w) || isNaN(h) || l <= 0 || w <= 0 || h <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }
    const volume = l * w * h;
    setResult(volume);
    toast({ title: "Volume Calculated", description: `V = ${volume.toFixed(2)} units³` });
  };

  const calculateSphere = () => {
    const r = parseFloat(radius);
    if (isNaN(r) || r <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid positive radius.",
        variant: "destructive",
      });
      return;
    }
    const volume = (4/3) * Math.PI * r * r * r;
    setResult(volume);
    toast({ title: "Volume Calculated", description: `V = ${volume.toFixed(2)} units³` });
  };

  const calculateCylinder = () => {
    const r = parseFloat(radius);
    const h = parseFloat(height);
    if (isNaN(r) || isNaN(h) || r <= 0 || h <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }
    const volume = Math.PI * r * r * h;
    setResult(volume);
    toast({ title: "Volume Calculated", description: `V = ${volume.toFixed(2)} units³` });
  };

  const reset = () => {
    setSide("");
    setLength("");
    setWidth("");
    setHeight("");
    setRadius("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Volume Calculator" 
      calculatorPath="/calculator/volume"
      category="math"
      subcategory="Geometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Volume Calculator</CardTitle>
            <CardDescription>
              Calculate volume for 3D shapes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="cube">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="cube">Cube</TabsTrigger>
                <TabsTrigger value="box">Box</TabsTrigger>
                <TabsTrigger value="sphere">Sphere</TabsTrigger>
                <TabsTrigger value="cylinder">Cylinder</TabsTrigger>
              </TabsList>

              <TabsContent value="cube" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="side">Side Length</Label>
                  <Input
                    id="side"
                    type="number"
                    step="any"
                    value={side}
                    onChange={(e) => setSide(e.target.value)}
                    placeholder="Enter side length"
                  />
                </div>
                <Button onClick={calculateCube} className="w-full">Calculate</Button>
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <MathFormula block>{String.raw`V = s^3`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="box" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="length">Length</Label>
                  <Input
                    id="length"
                    type="number"
                    step="any"
                    value={length}
                    onChange={(e) => setLength(e.target.value)}
                    placeholder="Enter length"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="width">Width</Label>
                  <Input
                    id="width"
                    type="number"
                    step="any"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    placeholder="Enter width"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height-box">Height</Label>
                  <Input
                    id="height-box"
                    type="number"
                    step="any"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="Enter height"
                  />
                </div>
                <Button onClick={calculateBox} className="w-full">Calculate</Button>
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <MathFormula block>{String.raw`V = l \times w \times h`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="sphere" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="radius-sphere">Radius</Label>
                  <Input
                    id="radius-sphere"
                    type="number"
                    step="any"
                    value={radius}
                    onChange={(e) => setRadius(e.target.value)}
                    placeholder="Enter radius"
                  />
                </div>
                <Button onClick={calculateSphere} className="w-full">Calculate</Button>
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <MathFormula block>{String.raw`V = \frac{4}{3}\pi r^3`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="cylinder" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="radius-cylinder">Radius</Label>
                  <Input
                    id="radius-cylinder"
                    type="number"
                    step="any"
                    value={radius}
                    onChange={(e) => setRadius(e.target.value)}
                    placeholder="Enter radius"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height-cylinder">Height</Label>
                  <Input
                    id="height-cylinder"
                    type="number"
                    step="any"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="Enter height"
                  />
                </div>
                <Button onClick={calculateCylinder} className="w-full">Calculate</Button>
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <MathFormula block>{String.raw`V = \pi r^2 h`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <Button onClick={reset} variant="outline" className="w-full">Reset All</Button>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Volume</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-3xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(2)} units³</p>
                    <CopyResultButton value={`${result.toFixed(2)} units³`} />
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
